import { uniq } from 'ramda';

import { OUTBOUND } from 'constants/booking';
import { CARRIER_CODE, mapCarrierCode } from 'constants/clubEurostar';
import { EUROSTAR_PLUS_NAME, EUROSTAR_PLUS_NAME_FOR_SF, STANDARD_NAME } from 'constants/classOfService';
import serviceTypeCodes, { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';
import { types as passengerTypes } from 'types/passengers';
import { splitLegsByDirection } from 'components/ui/ReviewBookingSummary/SegmentSummary/SegmentSummary.utils';
import GenericError from 'errors/GenericError';

const OUTBOUND_DIRECTION = 'OUTBOUND';
const INBOUND_DIRECTION = 'INBOUND';
const SINGLE_JOURNEY = 'SINGLE';

const NON_CHARGEABLE_PASSENGER_TYPES = [passengerTypes.INFANT, passengerTypes.DOG];

export const getSFClassNamesForUpgrade = ({ classOfServiceName = EUROSTAR_PLUS_NAME }) => {
  if (classOfServiceName === EUROSTAR_PLUS_NAME) return EUROSTAR_PLUS_NAME_FOR_SF;
  return classOfServiceName;
};

const describeLeg = (direction, leg) => {
  const chargeableItems = leg.items.filter((item) => !NON_CHARGEABLE_PASSENGER_TYPES.includes(item.passengerType));

  if (chargeableItems.some((item) => !item.bucketCode)) {
    throw new GenericError({ message: 'The upgraded journey is missing its fare buckets' }, { direction });
  }

  return {
    direction,
    carrier: leg.serviceTypeCode === serviceTypeCodes.EUROSTAR ? CARRIER_CODE.BLUE : CARRIER_CODE.RED,
    newTicketClass: getSFClassNamesForUpgrade({ classOfServiceName: leg.className }),
    classOfServiceCode: leg.classCode,
    tickets: chargeableItems.map(({ ticketNumber, bucketCode }) => ({ ticketNumber, bucketCode }))
  };
};

const describeJourney = ({ direction, legs }) => {
  const pricedLegs = legs.filter((leg) => EUROSTAR_CARRIERS.includes(leg.serviceTypeCode));

  if (!pricedLegs.length) {
    throw new GenericError({ message: 'No Eurostar train to price the upgrade' }, { direction });
  }

  return pricedLegs.map((leg) => describeLeg(direction, leg));
};

const describeLegs = (journeys) => {
  if (!journeys.length) {
    throw new GenericError({ message: 'No journeys selected for the upgrade' });
  }

  const described = journeys.flatMap(describeJourney);
  const [first] = described;
  const isConsistent = described.every(
    ({ newTicketClass, tickets }) => newTicketClass === first.newTicketClass && tickets.length === first.tickets.length
  );

  if (!isConsistent) {
    throw new GenericError(
      { message: 'Upgraded legs must share the class and passengers' },
      {
        legs: described
      }
    );
  }

  return described;
};

export const getUpgradeJourneysFromLegs = (legs = []) => {
  const [outboundLegs, inboundLegs] = splitLegsByDirection(legs);

  return [
    { direction: OUTBOUND_DIRECTION, legs: outboundLegs },
    { direction: INBOUND_DIRECTION, legs: inboundLegs }
  ]
    .filter((journey) => journey.legs.length)
    .map((journey) => ({
      direction: journey.direction,
      legs: journey.legs.map((leg) => ({
        serviceTypeCode: leg.serviceType?.code,
        className: leg.classOfService?.name,
        classCode: leg.classOfService?.code,
        items: (leg.bookingItems || [])
          .filter((item) => !item.cancelled && !item.isAFareAddOn)
          .map((item) => ({
            passengerType: item.passenger?.type,
            bucketCode: item.tariffDetails?.bucketCode,
            ticketNumber: item.ticketNumber
          }))
      }))
    }));
};

export const getUpgradeJourneysFromFares = (fares, passengers = []) =>
  fares.filter(Boolean).map((fare) => ({
    direction: fare.direction === OUTBOUND ? OUTBOUND_DIRECTION : INBOUND_DIRECTION,
    legs: fare.legs.map((leg) => ({
      serviceTypeCode: leg.serviceType?.code,
      className: leg.classOfService?.name || fare.classOfService?.name,
      classCode: leg.classOfService?.code || fare.classOfService?.code,
      items: (leg.products || [])
        .filter((product) => !product.isFareAddOn)
        .map((product) => ({
          passengerType: passengers.find((passenger) => passenger.id === product.passengerId)?.type,
          bucketCode: product.bucketCode
        }))
    }))
  }));

export const getTicketUpgradePricesParams = (journeys) => {
  const legs = describeLegs(journeys);
  const carriers = uniq(legs.map((leg) => leg.carrier));

  return {
    currentTicketClass: STANDARD_NAME,
    newTicketClass: legs[0].newTicketClass,
    ...(carriers.length === 1 && { carrierCode: mapCarrierCode[carriers[0]] })
  };
};

const getTicketPoints = (prices, { direction, carrier }, { bucketCode }) => {
  const points = prices.find(
    (price) => price.carrierCode === mapCarrierCode[carrier] && (price.bucketCodes == null || price.bucketCodes.includes(bucketCode))
  )?.points;

  if (!Number.isFinite(points) || points <= 0) {
    throw new GenericError({ message: 'No upgrade price for the fare buckets' }, { direction, carrier, bucketCode });
  }

  return points;
};

export const getUpgradeQuote = (journeys, prices = []) => {
  const legs = describeLegs(journeys);

  const points = legs.reduce(
    (total, leg) => leg.tickets.reduce((legTotal, ticket) => legTotal + getTicketPoints(prices, leg, ticket), total),
    0
  );

  const [{ classOfServiceCode, tickets }] = legs;

  return {
    points,
    carrier: legs.some((leg) => leg.carrier === CARRIER_CODE.RED) ? CARRIER_CODE.RED : CARRIER_CODE.BLUE,
    toTravelClassOfService: classOfServiceCode,
    numberOfPassengers: tickets.length,
    journeyType: SINGLE_JOURNEY,
    legsPerPassenger: legs.length,
    upgradeJourneys: legs.map((leg) => ({
      direction: leg.direction,
      toFareBucketCodes: uniq(leg.tickets.map((ticket) => ticket.bucketCode)),
      tickets: leg.tickets
    }))
  };
};
